import React from 'react'
import { Box, Tabs, Tab, styled } from '@mui/material'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

/**
 * タブアイテムの型定義
 */
export interface TabItem {
  id: string
  label: string
}

/**
 * TabSwitcherコンポーネントのプロパティ
 */
interface TabSwitcherProps {
  /**
   * タブの配列
   */
  tabs: TabItem[]

  /**
   * 現在選択されているタブのID
   */
  activeTab: string

  /**
   * タブ切り替え時に呼び出されるコールバック関数
   */
  onTabChange: (tabId: string) => void
}

// スタイル定義
const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: colorTokens.blue[600],
    height: 3,
    borderRadius: '3px 3px 0 0',
    bottom: 0,
  },
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(0.5),
  },
}))

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.typography.pxToRem(16),
  marginRight: theme.spacing(1),
  color: colorTokens.text.secondary,
  borderRadius: '12px 12px 0 0',
  padding: '10px 20px',
  minHeight: '44px',
  marginBottom: '0', // Remove gap with divider
  transition: 'all 0.2s ease',
  '&.Mui-selected': {
    color: colorTokens.blue[600],
    fontWeight: 600,
    backgroundColor: colorTokens.blue[50],
    borderBottom: `1px solid ${colorTokens.blue[50]}`,
  },
  '&:hover': {
    backgroundColor: colorTokens.action.primaryBgLight,
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.selected,
  },
}))

/**
 * タブ切り替え機能を提供するコンポーネント
 *
 * @param props TabSwitcherコンポーネントのプロパティ
 * @returns TabSwitcherコンポーネント
 */
const TabSwitcher: React.FC<TabSwitcherProps> = ({
  tabs,
  activeTab,
  onTabChange,
}) => {
  // タブ変更ハンドラー
  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    onTabChange(newValue)
  }

  return (
    <Box sx={{ width: 'auto', bgcolor: 'background.paper' }}>
      <StyledTabs
        value={activeTab}
        onChange={handleChange}
        aria-label="タブ切り替え"
        variant="standard"
      >
        {tabs.map((tab) => (
          <StyledTab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            id={`tab-${tab.id}`}
            aria-controls={`tabpanel-${tab.id}`}
          />
        ))}
      </StyledTabs>
    </Box>
  )
}

export default TabSwitcher
